import { useMemo, useState } from "react";

import moment from "moment-timezone";

import { TMyClass } from "../../../../../@types";
import { defaultTimezone } from "../../../../../constants/timezone";
import { useAccount } from "../../../../../hooks/useAccount";
import { useSchedule } from "../../../../../hooks/useSchedule";
import { useTeachingPolicy } from "../../../../../hooks/useTeachingPolicy";
import { ScheduleCalendar } from "../../../../common/ScheduleCalendar/ScheduleCalendar";
import { BookingLesson } from "./BookingLesson";

export type TCalendarProps = {
	onCellDoubleClick?: (startDate: Date, endDate: Date) => void;
};

export function Calendar({ onCellDoubleClick }: TCalendarProps) {
	const [selectedBooking, setSelectedBooking] = useState<TMyClass | null>(null);
	const { account } = useAccount();
	const { bookings, busyTimes, deleteBusyTime } = useSchedule();
	const { availableHours } = useTeachingPolicy();

	const scheduleData = useMemo(() => {
		const bookingAsInstructor = bookings
			.filter((booking) => booking.teacherId === account?.id)
			.map((booking) => ({
				...booking,
				startDate: new Date(
					moment(booking.startTime)
						.tz(account?.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				),
				endDate: new Date(
					moment(booking.endTime)
						.tz(account?.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				),
				isInstructor: true,
				canReschedule: new Date(booking.startTime ?? "") > new Date(),
			}));

		const bookingAsStudent = bookings
			.filter((booking) => booking.userId === account?.id)
			.map((booking) => ({
				...booking,
				startDate: new Date(
					moment(booking.startTime)
						.tz(account?.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				),
				endDate: new Date(
					moment(booking.endTime)
						.tz(account?.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				),
				user: {
					lastName: account?.lastName ?? "",
					firstName: account?.firstName ?? "",
				},
				isInstructor: false,
			}));

		const myBusyTimes = busyTimes.map((busyTime) => ({
			...busyTime,
			startDate: new Date(
				moment
					.utc(busyTime.startTime)
					.tz(account?.timezoneName ?? defaultTimezone)
					.format("YYYY-MM-DD HH:mm:ss.SSS"),
			),
			endDate: new Date(
				moment
					.utc(busyTime.endTime)
					.tz(account?.timezoneName ?? defaultTimezone)
					.format("YYYY-MM-DD HH:mm:ss.SSS"),
			),
			isMyBusyTime: true,
		}));

		return [...bookingAsInstructor, ...bookingAsStudent, ...myBusyTimes];
	}, [
		account?.firstName,
		account?.id,
		account?.lastName,
		account?.timezoneName,
		bookings,
		busyTimes,
	]);

	const onClickDeleteBusyTime = (id: number) => {
		deleteBusyTime(id);
	};

	const onClickReschedule = (myClass: TMyClass) => {
		setSelectedBooking({
			...myClass,
		});
	};

	return (
		<>
			<ScheduleCalendar
				data={scheduleData}
				onCellDoubleClick={onCellDoubleClick}
				onClickDeleteBusyTime={onClickDeleteBusyTime}
				onClickReschedule={onClickReschedule}
				showAvailability
				availableHours={availableHours}
			/>
			{selectedBooking && (
				<BookingLesson
					booking={selectedBooking}
					setSelectedBooking={setSelectedBooking}
				/>
			)}
		</>
	);
}
